import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import axiosInstance from "../../../utils/axiosInstance";

const PatientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchPatientDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/patients/${id}`,
      );

      setPatient(res.data.patient);
    } catch (err) {
      console.error("Error fetching patient details:", err);
      setError(
        err.response?.data?.message || "Failed to load patient details.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPatientDetails();
    }
  }, [id]);

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // WhatsApp message function
  const handleSendWhatsApp = () => {
    const phoneNumber = patient?.contactNo?.replace(/\D/g, ""); // Remove non-digits
    if (phoneNumber) {
      const message = `Hello ${patient?.fullName || "there"}, this is a message from the medical team.`;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
    } else {
      alert("No phone number available for this patient.");
    }
  };

  // View prescription in new tab
  const handleViewPrescription = (prescription) => {
    if (prescription.fileUrl) {
      window.open(prescription.fileUrl, "_blank");
    } else {
      alert("No file available for this prescription.");
    }
  };

  // Get status badge for prescriptions
  const getPrescriptionStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "PENDING_REVIEW":
        return "bg-yellow-100 text-yellow-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">
            Loading patient details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="AlertCircle"
            size={48}
            className="text-red-400 mx-auto mb-4"
          />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Error Loading Patient
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            onClick={() => navigate("/patient-management")}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="UserX" size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Patient Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The patient you're looking for doesn't exist.
          </p>
          <Button
            onClick={() => navigate("/patient-management")}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4">
          <Breadcrumb />

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="User" size={24} className="text-blue-600" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {patient.fullName || "Unknown Patient"}
                </h1>
                <p className="text-gray-600 text-sm mt-1">
                  Patient ID: {patient.id}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      patient.status === "active"
                        ? "bg-green-100 text-green-800"
                        : patient.status === "inactive"
                          ? "bg-gray-100 text-gray-800"
                          : "bg-red-100 text-red-800"
                    }`}>
                    {patient.status}
                  </span>
                  {patient.gender && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {patient.gender}
                    </span>
                  )}
                  {patient.bloodType && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Blood: {patient.bloodType}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/patient-management")}
                className="text-xs">
                <Icon name="ArrowLeft" size={14} className="mr-1" />
                Back to List
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {["overview", "medical", "prescriptions", "orders", "family"].map(
                (tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs ${
                      activeTab === tab
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {tab === "prescriptions" &&
                      patient.prescriptions?.length > 0 && (
                        <span className="ml-1 bg-blue-100 text-blue-600 rounded-full px-1.5 py-0.5 text-xs">
                          {patient.prescriptions.length}
                        </span>
                      )}
                    {tab === "orders" && patient.orders?.length > 0 && (
                      <span className="ml-1 bg-blue-100 text-blue-600 rounded-full px-1.5 py-0.5 text-xs">
                        {patient.orders.length}
                      </span>
                    )}
                  </button>
                ),
              )}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Overview Tab */}
              {activeTab === "overview" && (
                <>
                  {/* Personal Information */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      <div>
                        <label className="font-medium text-gray-700">
                          Full Name
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.fullName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Email
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.email || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Contact Number
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.contactNo || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Date of Birth
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.dob
                            ? `${formatDate(patient.dob)} (${calculateAge(patient.dob)} years)`
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Gender
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.gender || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Relationship
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.relationship || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Health Information */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Health Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <label className="font-medium text-gray-700">
                          Blood Type
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.bloodType || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Height
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.height || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Weight
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.weight || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Age</label>
                        <p className="text-gray-900 mt-1">
                          {calculateAge(patient.dob)} years
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lifestyle Information */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Lifestyle Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                      <div>
                        <label className="font-medium text-gray-700">
                          Smoking Habit
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.smokingHabit || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Alcohol Consumption
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.alcoholConsumption || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">
                          Exercise Frequency
                        </label>
                        <p className="text-gray-900 mt-1">
                          {patient.exerciseFrequency || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Prescriptions Tab */}
              {activeTab === "prescriptions" && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Prescriptions
                  </h3>
                  {patient.prescriptions?.length > 0 ? (
                    <div className="space-y-3">
                      {patient.prescriptions.map((prescription) => (
                        <div
                          key={prescription.id}
                          className="border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="text-xs font-medium text-gray-900">
                                Prescription #{prescription.id}
                              </h4>
                              <p className="text-xs text-gray-500">
                                Uploaded:{" "}
                                {formatDateTime(prescription.uploadedAt)}
                              </p>
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPrescriptionStatusBadge(prescription.status)}`}>
                              {prescription.status.replace("_", " ")}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            <div>
                              <span className="text-gray-600">File Type:</span>
                              <span className="ml-1 text-gray-900">
                                {prescription.fileType}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">
                                Reviewed By:
                              </span>
                              <span className="ml-1 text-gray-900">
                                {prescription.reviewedBy?.name ||
                                  "Not reviewed"}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="solid"
                              size="sm"
                              iconName="Eye"
                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                              onClick={() =>
                                handleViewPrescription(prescription)
                              }>
                              View Prescription
                            </Button>
                            {prescription.remarks && (
                              <div className="text-xs text-gray-600">
                                <strong>Remarks:</strong> {prescription.remarks}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Icon
                        name="FileText"
                        size={32}
                        className="text-gray-400 mx-auto mb-2"
                      />
                      <p className="text-gray-500 text-sm">
                        No prescriptions found
                      </p>
                    </div>
                  )}
                </div>
              )}

              {patient.orders.map((order) => (
                <div
                  key={order.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      navigate(`/orders/${order.id}`);
                  }}
                  className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-xs font-medium text-gray-900">
                        Order #{order.id}
                      </h4>
                      <p className="text-xs text-gray-500">
                        Created: {formatDateTime(order.createdAt)}
                      </p>
                    </div>

                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : order.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.status === "cancelled"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                      }`}>
                      {order.status}
                    </span>
                  </div>

                  <div className="text-xs text-gray-600">
                    <p className="text-gray-500">Click to view order details</p>
                  </div>
                </div>
              ))}

              {/* Family Tab */}
              {activeTab === "family" && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Family Members
                  </h3>
                  {patient.familyMembers?.length > 0 ? (
                    <div className="space-y-3">
                      {patient.familyMembers.map((member) => (
                        <div
                          key={member.id}
                          className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <Icon
                                name="User"
                                size={14}
                                className="text-blue-600"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xs font-medium text-gray-900">
                                {member.fullName}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {member.relationship} •{" "}
                                {calculateAge(member.dob)} years •{" "}
                                {member.gender}
                              </p>
                            </div>
                            <div className="text-xs text-gray-600">
                              {member.contactNo}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Icon
                        name="Users"
                        size={32}
                        className="text-gray-400 mx-auto mb-2"
                      />
                      <p className="text-gray-500 text-sm">
                        No family members found
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Status Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Account Status
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span
                      className={`font-medium ${
                        patient.status === "active"
                          ? "text-green-600"
                          : patient.status === "inactive"
                            ? "text-gray-600"
                            : "text-red-600"
                      }`}>
                      {patient.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Member Since</span>
                    <span className="text-gray-900">
                      {formatDate(patient.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated</span>
                    <span className="text-gray-900">
                      {formatDate(patient.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Quick Actions
                </h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs"
                    iconName="FileText"
                    onClick={() => setActiveTab("prescriptions")}>
                    View Prescriptions ({patient.prescriptions?.length || 0})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs"
                    iconName="ShoppingCart"
                    onClick={() => setActiveTab("orders")}>
                    View Orders ({patient.orders?.length || 0})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs"
                    iconName="MessageSquare"
                    onClick={handleSendWhatsApp}>
                    Send WhatsApp Message
                  </Button>
                </div>
              </div>

              {/* Primary Contact */}
              {patient.primary && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Primary Contact
                  </h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center">
                        <Icon
                          name="User"
                          size={12}
                          className="text-green-600"
                        />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {patient.primary.fullName}
                        </p>
                        <p className="text-gray-500">
                          {patient.primary.contactNo}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PatientDetails;
